import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { getReceipt, type ReceiptDetailView } from '../receipts.api';

export type ReceiptDetailState =
  { status: 'loading' } | { status: 'loaded'; receipt: ReceiptDetailView } | { status: 'forbidden' } | { status: 'not_found' } | { status: 'error' };

function toErrorState(err: unknown): ReceiptDetailState {
  if (err instanceof ApiError && err.status === 403) return { status: 'forbidden' };
  if (err instanceof ApiError && err.status === 404) return { status: 'not_found' };
  return { status: 'error' };
}

/**
 * One receipt, fetched by id. A receipt is never edited, but clearing, bouncing or reversing it changes its status and chain, so `reload`
 * fetches it again. A failed reload keeps what is on screen: the change itself already succeeded or was refused.
 */
export function useReceiptDetail(id: string) {
  const [state, setState] = useState<ReceiptDetailState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    getReceipt(id)
      .then((receipt) => {
        if (!cancelled) setState({ status: 'loaded', receipt });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const reload = useCallback(async () => {
    try {
      setState({ status: 'loaded', receipt: await getReceipt(id) });
    } catch {
      // The old receipt stays up.
    }
  }, [id]);

  return { state, reload };
}
